import { useState, useEffect, useCallback, useRef } from 'react';
import PushNotificationsService from '../services/pushNotifications.service';
import PreferencesService from '../services/preferences.service';
import UserService from '../services/user.service';
import { useAuth } from '../AuthProvider';
import { useAppState } from '../AppListenerProvider';
import { useContext } from 'react';
import { AppContext } from '../AppStateProvider';
import { Events, Params, useAnalytics } from '../AppAnalyticsProvider';

/**
 * Best-effort mapping from a push notification's redirect_url to a stable
 * destination_screen value for the push CTR dashboard. We bucketize the URL
 * path into low-cardinality categories rather than logging the raw URL
 * (which is high-cardinality and can leak IDs).
 */
const bucketizeDestination = (redirectUrl: string | undefined): string => {
    if (!redirectUrl) return 'none';
    try {
        const path = redirectUrl.startsWith('http') ? new URL(redirectUrl).pathname : redirectUrl;
        if (path.startsWith('/profile')) return 'profile';
        if (path.startsWith('/clip')) return 'clip';
        if (path.startsWith('/home')) return 'home';
        if (path.startsWith('/community')) return 'community';
        if (path.startsWith('/crews')) return 'crew';
        if (path.startsWith('/event')) return 'event';
        if (path.startsWith('/shop')) return 'shop';
        if (path.startsWith('/stats')) return 'stats';
        return 'other';
    } catch {
        return 'invalid';
    }
};

/** Map the notification payload to a bounded notification_type for breakdowns. */
const derivePushType = (data: Record<string, any> | undefined): string => {
    if (!data) return 'unknown';
    // Firebase payloads often carry a `type` or `notification_type` key — prefer
    // explicit, fall back to inference from the redirect URL.
    if (typeof data.type === 'string') return data.type;
    if (typeof data.notification_type === 'string') return data.notification_type;
    const url = typeof data.redirect_url === 'string' ? data.redirect_url : '';
    if (url.includes('/clip')) return 'trick_activity';
    if (url.includes('/profile')) return 'profile_activity';
    if (url.includes('/shop')) return 'shop';
    return 'generic';
};

export type PushPermissionStatus = 'prompt' | 'granted' | 'denied' | 'prompt-with-rationale';

/**
 * Module-level coordination shared by every mounted instance of this hook.
 *
 * This hook is instantiated dozens of times at once (every feed/board card via
 * useAuthService, plus AppMain and ExplorePage), and each instance re-runs its
 * permission effect when the app returns to the foreground. Without
 * coordination, one foreground transition fires 40–50 duplicate
 * FirebaseMessaging.checkPermissions/getToken bridge calls and as many
 * Preferences reads. Coordination gives exactly one native check per
 * foreground activation:
 *
 * - `inFlightPushCheck` — concurrent callers share one in-flight check.
 * - `activationPushStatus` — instances that check later in the same activation
 *   reuse the settled result. Cleared when the app backgrounds, so the next
 *   foreground re-checks (permissions can only change while backgrounded).
 * - `pushActivationEpoch` — guards against a check that started before the app
 *   backgrounded caching its (potentially stale) result into the next activation.
 */
let inFlightPushCheck: Promise<PushPermissionStatus | null> | null = null;
let activationPushStatus: PushPermissionStatus | null = null;
let pushActivationEpoch = 0;

export const __resetPushCheckCoordinationForTests = () => {
    inFlightPushCheck = null;
    activationPushStatus = null;
    pushActivationEpoch = 0;
};

export const usePushNotifications = () => {
    const [permissionStatus, setPermissionStatus] = useState<PushPermissionStatus>('prompt');
    const { user, isAuthenticated } = useAuth();
    const { isNative, isActive, platform } = useAppState();
    const { dispatch } = useContext(AppContext);
    const { trackEvent } = useAnalytics();

    /**
     * Maps notification_id -> received timestamp so the push_opened event can
     * carry seconds_since_received (engagement latency metric). Ref, not state,
     * so updates don't trigger re-renders.
     */
    const pushReceivedAtRef = useRef<Map<string, number>>(new Map());

    const initNotificationsHelper = async (token: string) => {
        await PushNotificationsService.addTokenReceivedListener(fcmTokenReceived);
        await PushNotificationsService.addNotificationReceivedListener(notificationReceived);
        await PushNotificationsService.addNotificationActionPerformedListener(notificationActionPerformed);
        if (isAuthenticated) {
            console.log("FCM TOKEN RECEIVED AND USER IS AUTHENTICATED");
            const res = await UserService.updateUserPushNotifications(user?.uid, token);
            if (res) {
                await UserService.setUser(true, dispatch);
                console.log("SET THE USER WITH THE NEW FCN TOKEN");
            }
        }
    }

    const fcmTokenReceived = async (event: any) => {
        console.log("FCM TOKEN RECEIVED");
        await PreferencesService.setPushNotificationToken(event.token);
        if (isAuthenticated) {
            console.log("FCM TOKEN RECEIVED AND USER IS AUTHENTICATED");
            const res = await UserService.updateUserPushNotifications(user?.uid, event.token);
            if (res) {
                await UserService.setUser(true, dispatch);
                console.log("SET THE USER WITH THE NEW FCM TOKEN");
            }
        }
    };

    const notificationReceived = (event: any) => {
        console.log("NOTIFICATION RECEIVED", event);

        const notifId: string = event?.notification?.id ?? event?.notification?.data?.notification_id ?? '';
        if (notifId) {
            pushReceivedAtRef.current.set(notifId, Date.now());
        }

        trackEvent(Events.PUSH_RECEIVED, {
            [Params.NOTIFICATION_TYPE]: derivePushType(event?.notification?.data),
            [Params.DELIVERED_IN_FOREGROUND]: isActive ? 'true' : 'false',
            [Params.PLATFORM]: platform,
        });
    };

    const notificationActionPerformed = async (event: any) => {
        console.log("NOTIFICATION ACTION PERFORMED", event);
        if (event.actionId === "tap") {
            await PushNotificationsService.removeAllDeliveredNotifications();

            const data = event?.notification?.data ?? {};
            const redirectUrl: string = data.redirect_url ?? '';
            const notificationType = derivePushType(data);
            const destinationScreen = bucketizeDestination(redirectUrl);

            // Seconds between receive and tap — drives the "engagement latency"
            // panel on the push dashboard.
            const notifId: string = event?.notification?.id ?? data.notification_id ?? '';
            const receivedAt = notifId ? pushReceivedAtRef.current.get(notifId) : undefined;
            const secondsSinceReceived = receivedAt
                ? Math.round((Date.now() - receivedAt) / 1000)
                : undefined;
            if (notifId) pushReceivedAtRef.current.delete(notifId);

            trackEvent(Events.PUSH_OPENED, {
                [Params.NOTIFICATION_TYPE]: notificationType,
                [Params.DESTINATION_SCREEN]: destinationScreen,
                [Params.PLATFORM]: platform,
                ...(secondsSinceReceived !== undefined
                    ? { [Params.SECONDS_SINCE_RECEIVED]: secondsSinceReceived }
                    : {}),
            });

            if (redirectUrl && redirectUrl !== "") {
                dispatch({ type: "setPushNotificationRedirectUrl", payload: redirectUrl });

                // Approximate destination_reached at dispatch time. A true
                // "destination reached" would fire after the router settles on
                // the target page, which would require coupling the router to
                // analytics. For the dashboard funnel, the dispatch-time signal
                // is sufficient — drop-off between PUSH_OPENED and this event
                // indicates a navigation failure (malformed URL, etc.).
                trackEvent(Events.PUSH_DESTINATION_REACHED, {
                    [Params.NOTIFICATION_TYPE]: notificationType,
                    [Params.DESTINATION_SCREEN]: destinationScreen,
                    [Params.PLATFORM]: platform,
                });
            }
        }
    };

    const upsertNotificationsInDb = async () => {
        const token = await PushNotificationsService.getToken();
        const tokenInStorage = await PreferencesService.getPushNotificationToken();
        return tokenInStorage !== token;
    }

    const initNotifications = async () => {
        const token = await PushNotificationsService.getToken();
        await PreferencesService.setPushNotificationToken(token);
        await PushNotificationsService.removeAllListeners();
        await initNotificationsHelper(token);
    }

    const requestPushNotificationPermission = async () => {
        if (isNative) {
            console.log("REQUESTING PUSH NOTIFICATION PERMISSION");

            // Fire prompt-shown BEFORE the OS dialog appears so the denominator
            // of the permission funnel (prompts shown) is accurate even if the
            // request call itself throws.
            trackEvent(Events.PERMISSION_PROMPT_SHOWN, {
                [Params.PERMISSION_TYPE]: 'push',
                [Params.PLATFORM]: platform,
            });

            const requestedPerms = await PushNotificationsService.requestPermissions();
            setPermissionStatus(requestedPerms);
            // Keep the activation-shared status fresh so instances mounting after
            // a user-driven grant/deny don't reuse the pre-request result.
            activationPushStatus = requestedPerms;

            // Structured permission_result event — the permissions dashboard
            // uses this to compute grant/deny rates per permission_type and
            // platform. Existing `location_permission_result` event stays as-is
            // for backwards compat of that specific screen's analytics.
            trackEvent(Events.PERMISSION_RESULT, {
                [Params.PERMISSION_TYPE]: 'push',
                [Params.PERMISSION_STATUS]: requestedPerms,
                [Params.PLATFORM]: platform,
            });
            // Also fire the legacy-named event used by other code paths so we
            // have one canonical event name for the permissions panel.
            trackEvent(Events.NOTIFICATION_PERMISSION_REQUESTED, {
                [Params.PERMISSION_STATUS]: requestedPerms,
                [Params.PLATFORM]: platform,
            });

            if (requestedPerms === "granted") {
                await initNotifications();
            }
        }
    };

    /**
     * The actual native round — only ever executed by the one instance that
     * starts a check; everyone else shares its promise/result (see the
     * module-level coordination above).
     */
    const runPushCheck = async (): Promise<PushPermissionStatus | null> => {
        console.log("Checking push notifications");
        let permissions: PushPermissionStatus;
        try {
            permissions = await PushNotificationsService.checkPermissions();
        } catch (error) {
            console.error("Push notification permission check failed:", error);
            return null;
        }

        if (permissions === "granted") {
            try {
                const shouldUpdateToken = await upsertNotificationsInDb();
                console.log("Should update push notification token:", shouldUpdateToken);

                if (shouldUpdateToken) {
                    console.log("Updating push notification token");
                    await initNotifications();
                }
            } catch (error) {
                // Token bookkeeping failing must not take the permission status
                // down with it — the next foreground re-checks anyway.
                console.error("Push notification token refresh failed:", error);
            }
        }
        return permissions;
    };

    const checkAndUpdatePushNotifications = useCallback(async () => {
        if (!isNative) {
            return;
        }

        if (!isActive) {
            // Backgrounding ends the activation: drop the cached result so the
            // next foreground performs exactly one fresh check.
            activationPushStatus = null;
            pushActivationEpoch++;
            return;
        }

        if (activationPushStatus !== null) {
            setPermissionStatus(activationPushStatus);
            return;
        }

        if (!inFlightPushCheck) {
            const epochAtStart = pushActivationEpoch;
            inFlightPushCheck = runPushCheck()
                .then((permissions) => {
                    if (permissions !== null && epochAtStart === pushActivationEpoch) {
                        activationPushStatus = permissions;
                    }
                    return permissions;
                })
                .finally(() => {
                    inFlightPushCheck = null;
                });
        }

        const permissions = await inFlightPushCheck;
        if (permissions !== null) {
            setPermissionStatus(permissions);
        }
    }, [isNative, isActive, isAuthenticated, user]);

    useEffect(() => {
        checkAndUpdatePushNotifications();
    }, [checkAndUpdatePushNotifications]);

    return {
        permissionStatus,
        requestPushNotificationPermission,
        checkPushNotificationPermission: checkAndUpdatePushNotifications,
    };
};
